'use client'
/**
 * Sarah launcher + listing-detail teaser.
 *
 * Bottom-end disc with the approved avatar. After ~30s on a listing detail
 * page only, a small teaser: desktop bubble with her photo and copy, phone
 * a badge on the disc. Never auto-opens the panel. Session dismiss.
 * prefers-reduced-motion is honored in CSS (duration tokens + a media query).
 *
 * Muse audit 2026-10-20 (C1): the disc opens the text chat. Voice starts only
 * from the mic in the composer, so opening never asks for the microphone. The
 * disc shrinks while the page scrolls and, once it settles, lifts above or
 * tucks away from any page control it would cover (lib/sarah/launcher-avoid).
 */
import { useCallback, useEffect, useId, useRef, useState, type CSSProperties, type PointerEvent } from 'react'
import { Mic } from 'lucide-react'
import { usePathname, useRouter } from 'next/navigation'
import { captureSarahLead } from '@/app/actions/sarah-capture'
import { withPostedGaIds } from '@/lib/analytics/ga-browser-ids'
import { answerSarahChat } from '@/app/actions/sarah-chat'
import { recordSarahConsent } from '@/app/actions/sarah-consent'
import { getSarahDeskBroker } from '@/app/actions/sarah-desk'
import { recordSarahFunnelStage } from '@/app/actions/sarah-funnel'
import { mintSarahVoiceToken } from '@/app/actions/sarah-voice'
import { logWebRecordingObjection } from '@/app/actions/sarah-recording'
import { decideSarahConsent } from '@/lib/sarah/lead-consent'
import {
  clearSarahVoiceConsent,
  grantSarahVoiceConsent,
  withoutVerbatimAnswers,
} from '@/lib/sarah/web-voice-consent'
import { V3_ROOT_CLASS } from './atoms'
import { SARAH_AVATAR_ALT, SARAH_AVATAR_SRC, SARAH_AVATAR_SRCSET } from '@/lib/sarah/assets'
import {
  deskBrokerFromAssigned,
  type SarahDeskBroker,
} from '@/lib/sarah/desk-broker'
import { evaluateSarahTurn } from '@/lib/sarah/guardrails'
import {
  afterHelpfulAnswer,
  applyIntentTurn,
  initialFlow,
  matchIntentChip,
  SARAH_INTENT_CHIPS,
  WEB_BRINGS_YOU_HERE_EN,
  WEB_DISCLOSURE_LINE_EN,
  type FlowChip,
  type FlowState,
} from '@/lib/sarah/intent-flow'
import {
  applyHomeSearchTurn,
  homeSearchFlowAnswers,
  looksLikeHomeSearch,
  type HomeSearchCard,
  type HomeSearchState,
} from '@/lib/sarah/home-search-parse'
import {
  SARAH_NAVIGATE_VOICE,
  isPublicSarahHref,
  looksLikeNavigate,
  resolveSarahNavigate,
} from '@/lib/sarah/navigate'
import {
  SARAH_EDGE_TAB_H,
  SARAH_PAINT_BAND_RIGHT,
  SARAH_EDGE_TAB_SLIM_HIT,
  SARAH_EDGE_TAB_W,
  SARAH_SCROLL_SETTLE_MS,
  placeLauncher,
  type Box as LauncherBox,
  type EdgeTabSlot,
  type LauncherPlacement,
  placeEdgeTab,
} from '@/lib/sarah/launcher-avoid'
import { sarahPaintedRects } from '@/lib/sarah/painted-content'
import { boxCoversContent } from '@/components/cookie-cog-placement'
import { listingUnknownFactEn, shouldAnswerFromPage } from '@/lib/sarah/listing-answer'
import { answerSiteQuestion } from '@/lib/sarah/site-answer'
import {
  DEFAULT_DESK_BROKER_FIRST_NAME,
  SARAH_NAME,
  WEB_HELP_EN,
  WEB_VOICE_HINT_END_EN,
  WEB_VOICE_HINT_LISTEN_EN,
  WEB_VOICE_HINT_SPEAK_EN,
  WEB_VOICE_HINT_THINK_EN,
  WEB_VOICE_NOTICE_EN,
  WEB_VOICE_OBJECTION_FAILED_EN,
  WEB_VOICE_OFF_FOR_CHAT_EN,
  WEB_VOICE_OBJECTION_STOPPED_EN,
  WEB_VOICE_START_EN,
  WEB_VOICE_TYPE_INSTEAD_EN,
  webGreetingEn,
} from '@/lib/sarah/scripts'
import { SarahHomeSearchCards } from './SarahHomeSearchCards'
import { SarahLauncherVideo } from './SarahLauncherVideo.client'
import {
  SARAH_TEASER_COPY,
  SARAH_TEASER_DELAY_MS,
  SARAH_TEASER_WIDE_MQ,
  listingTeaserKind,
  persistTeaserDismissed,
  shouldOfferListingTeaser,
  teaserDismissedInSession,
  type SarahTeaserKind,
} from '@/lib/sarah/teaser'
import { startSarahVoiceSession, type SarahVoiceHandle, type SarahVoiceStatus } from '@/lib/sarah/web-voice'
import {
  beginSarahVoiceFromMicTap,
  continueSarahVoiceAfterArm,
  logSarahVoiceError,
  releaseSarahArmedMic,
  sarahVoiceErrorMessage,
  takeVoiceErrorSlot,
  WEB_VOICE_START_FAIL_EN,
  type SarahArmedMic,
} from '@/lib/sarah/web-voice-arm'
import { shouldHidePublicChrome } from '@/lib/site/public-chrome-hide'
import { trackEvent } from '@/lib/tracking'
import {
  clearLauncherMinimized,
  readLauncherMinimized,
  writeLauncherMinimized,
} from '@/lib/sarah/launcher-minimize'
import './tokens.css'
import './V3SarahAssistant.css'

type ChatLine = {
  from: 'sarah' | 'visitor'
  text: string
  notice?: string
  voice?: boolean
  cards?: HomeSearchCard[]
  seeAll?: { href: string; label: string } | null
}

function sessionStore(): Storage | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    return sessionStorage
  } catch {
    return null
  }
}

/** After a recording objection, send only structured lead fields: no free-text words. */
function leadAnswersFor<T extends { question?: string; helpFinding?: string }>(answers: T, noRecord: boolean): T {
  return noRecord ? withoutVerbatimAnswers(answers) : answers
}

function visitorSessionId(): string | null {
  try {
    return localStorage.getItem('rr_session_id')
  } catch {
    return null
  }
}

function greetingLines(brokerFirstName = DEFAULT_DESK_BROKER_FIRST_NAME): ChatLine[] {
  const text =
    brokerFirstName.trim() === DEFAULT_DESK_BROKER_FIRST_NAME
      ? WEB_BRINGS_YOU_HERE_EN
      : webGreetingEn(brokerFirstName)
  return [{ from: 'sarah', text, notice: WEB_DISCLOSURE_LINE_EN }]
}

function samePlacement(a: LauncherPlacement, b: LauncherPlacement): boolean {
  if (a.mode !== b.mode) return false
  return a.mode !== 'lifted' || (b.mode === 'lifted' && a.lift === b.lift)
}

function SarahFace({
  className,
  size,
  alt = '',
}: {
  className?: string
  size: number
  alt?: string
}) {
  return (
    <img
      className={className}
      src={SARAH_AVATAR_SRC}
      srcSet={SARAH_AVATAR_SRCSET}
      sizes={`${size}px`}
      alt={alt}
      width={size}
      height={size}
      // The launcher face is never the LCP element. Without these React
      // preloads it in the head, and the 2x/3x file (49 to 92 KB) loads ahead
      // of the page's own hero image (search-ai P10 speed).
      loading="lazy"
      decoding="async"
      fetchPriority="low"
    />
  )
}

export function V3SarahAssistant() {
  const pathname = usePathname()
  const router = useRouter()
  const hidden = shouldHidePublicChrome(pathname)
  const titleId = useId()
  const [panelOpen, setPanelOpen] = useState(false)
  const [wide, setWide] = useState(false)
  const [teaserOn, setTeaserOn] = useState(false)
  const [dismissed, setDismissed] = useState(false)
  const [draft, setDraft] = useState('')
  const [greeted, setGreeted] = useState(true)
  const [desk, setDesk] = useState<SarahDeskBroker>(() => deskBrokerFromAssigned(null))
  const [lines, setLines] = useState<ChatLine[]>(() => greetingLines())
  const [flow, setFlow] = useState<FlowState>(() => initialFlow())
  const [chips, setChips] = useState<FlowChip[] | null>([...SARAH_INTENT_CHIPS])
  const [sheetDy, setSheetDy] = useState(0)
  const [voiceStatus, setVoiceStatus] = useState<SarahVoiceStatus>('idle')
  const [voiceMuted, setVoiceMuted] = useState(false)
  const [caption, setCaption] = useState('')
  const [scrolling, setScrolling] = useState(false)
  const [placement, setPlacement] = useState<LauncherPlacement>({ mode: 'rest' })
  // C1 edge tab (2026-10-21): when no lift clears the full disc, the launcher
  // collapses to the edge tab instead of vanishing. Not the visitor's choice,
  // so it is never stored and it returns to the disc once a clear spot exists.
  const [collapsed, setCollapsed] = useState(false)
  const collapsedRef = useRef(false)
  const minimizedRef = useRef(false)
  // The disc's last measured resting geometry, kept while the tab shows so the
  // disc can be tested without rendering it.
  const discGeomRef = useRef<{ w: number; h: number; rightGap: number; extras: LauncherBox[] } | null>(null)
  const [tabSlot, setTabSlot] = useState<(EdgeTabSlot & { dx: number; dy: number }) | null>(null)
  const [minimized, setMinimized] = useState(false)
  // Matt (2026-10-22, 5:47 and 6:33 AM PT): the launcher stays put and always
  // visible at every width. No fade or hide while scrolling, no edge tab, no
  // minimize X. Content keeps clear through reserved bottom space (body::after
  // in the CSS), not by moving the disc.
  const stable = SARAH_LAUNCHER_STABLE
  const restoreTabRef = useRef<HTMLButtonElement | null>(null)
  const focusTabRef = useRef(false)
  const panelId = useId()
  const shownRef = useRef(false)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const dragRef = useRef<{ y: number; dy: number } | null>(null)
  const voiceRef = useRef<SarahVoiceHandle | null>(null)
  const voiceErrorPostedRef = useRef({ posted: false })
  const voiceStartingRef = useRef(false)
  // Counsel 2026-10-22: the consent step shows before every mic start.
  const [voiceConsentOpen, setVoiceConsentOpen] = useState(false)
  // After a recording objection: voice stays off for this chat and no new
  // verbatim content from the session is stored.
  const noRecordRef = useRef(false)
  const personIdRef = useRef<number | null>(null)
  const pendingObjectionRef = useRef<'voice' | 'typed' | null>(null)
  const homeRef = useRef<HomeSearchState | null>(null)
  const skipVoiceSarahRef = useRef(false)
  const flowRef = useRef(flow)
  const messagesRef = useRef<HTMLDivElement | null>(null)
  const latestRef = useRef<HTMLDivElement | null>(null)
  flowRef.current = flow
  const teaserKind: SarahTeaserKind = listingTeaserKind(wide)
  const offer = shouldOfferListingTeaser({
    pathname,
    dismissed,
    panelOpen,
  })

  useEffect(() => {
    setDismissed(teaserDismissedInSession(sessionStore()))
  }, [])

  useEffect(() => {
    let cancelled = false
    void getSarahDeskBroker().then((got) => {
      if (cancelled || !got.data) return
      setDesk(got.data)
      setLines((prev) => {
        if (prev.length !== 1 || prev[0].from !== 'sarah') return prev
        return greetingLines(got.data.firstName)
      })
    })
    return () => {
      cancelled = true
    }
  }, [])

  const scrollToLatest = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const end = latestRef.current
    const box = messagesRef.current
    if (end && typeof end.scrollIntoView === 'function') {
      end.scrollIntoView({ block: 'end', behavior })
      return
    }
    if (box) box.scrollTop = box.scrollHeight
  }, [])

  useEffect(() => {
    if (!panelOpen) return
    scrollToLatest(lines.length <= 1 ? 'auto' : 'smooth')
  }, [lines, chips, panelOpen, scrollToLatest])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia(SARAH_TEASER_WIDE_MQ)
    const sync = () => setWide(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])

  // Muse audit C1: the closed disc never sits on a page control. It shrinks
  // while the page scrolls; once scrolling settles it measures the controls
  // under its resting box and lifts above them, or tucks away if no lift clears.
  useEffect(() => {
    if (hidden || panelOpen || typeof window === 'undefined') {
      setScrolling(false)
      setPlacement((prev) => (prev.mode === 'rest' ? prev : { mode: 'rest' }))
      collapsedRef.current = false
      setCollapsed(false)
      setTabSlot(null)
      return
    }
    if (stable) {
      setScrolling(false)
      setPlacement((prev) => (prev.mode === 'rest' ? prev : { mode: 'rest' }))
      collapsedRef.current = false
      setCollapsed(false)
      setTabSlot(null)
      if (rootRef.current) rootRef.current.style.visibility = ''
      return
    }
    let rafId = 0
    let settleId = 0
    const place = () => {
      rafId = 0
      const root = rootRef.current
      if (!root) return
      // The root's own box ignores the dock's translate: this is where the disc
      // rests, not where it stands now.
      const r = root.getBoundingClientRect()
      if (r.width <= 0 || r.height <= 0) return
      const vw = window.innerWidth
      const vh = window.innerHeight
      // Everything painted where the disc or the edge tab could sit.
      const controls = sarahPaintedRects(document, root, [
        { left: vw - SARAH_PAINT_BAND_RIGHT, right: vw, top: 0, bottom: vh },
        { left: 0, right: SARAH_EDGE_TAB_W, top: 0, bottom: vh },
      ]) as LauncherBox[]
      const showingTab = minimizedRef.current || collapsedRef.current
      if (!showingTab) {
        // The dismiss X moves with the disc, so it must clear controls too.
        // Kept as an offset from the disc's resting bottom-right corner.
        const extras: LauncherBox[] = []
        const dock = root.querySelector('[data-sarah="dock"]')
        const x = root.querySelector('[data-sarah="launcher-dismiss"]')
        if (dock && x) {
          const d = dock.getBoundingClientRect()
          const xb = x.getBoundingClientRect()
          extras.push({
            left: xb.left - r.right,
            right: xb.right - r.right,
            top: -(d.bottom - xb.top),
            bottom: -(d.bottom - xb.bottom),
          })
        }
        discGeomRef.current = { w: r.width, h: r.height, rightGap: vw - r.right, extras }
      }
      const tabBox = {
        left: vw - SARAH_EDGE_TAB_W,
        right: vw,
        top: r.bottom - SARAH_EDGE_TAB_H,
        bottom: r.bottom,
      }
      const placeTab = () =>
        placeLauncher({ rest: tabBox, controls, viewportHeight: vh })
      let next: LauncherPlacement
      let nextCollapsed = false
      let nextSlot: (EdgeTabSlot & { dx: number; dy: number }) | null = null
      if (minimizedRef.current) {
        // The visitor chose the tab. It still never covers a control: it lifts,
        // or as a last resort steps aside until the next scroll.
        next = placeTab()
      } else {
        const g = discGeomRef.current
        const right = g ? vw - g.rightGap : r.right
        // The root never moves for the tab (only its dock translates), so its
        // bottom is always the true resting bottom.
        const restBottom = r.bottom
        const disc = g
          ? { left: right - g.w, right, top: restBottom - g.h, bottom: restBottom }
          : { left: r.left, top: r.top, right: r.right, bottom: r.bottom }
        const extras = (g?.extras ?? []).map((e) => ({
          left: right + e.left,
          right: right + e.right,
          top: restBottom + e.top,
          bottom: restBottom + e.bottom,
        }))
        next = placeLauncher({ rest: disc, controls, viewportHeight: vh, extras })
        if (next.mode === 'tucked') {
          nextCollapsed = true
          // Slide along the right edge, then the left, then slim; else hide.
          const slot = placeEdgeTab({ controls, viewportWidth: vw, viewportHeight: vh, restBottom })
          if (slot) {
            const w = slot.slim ? SARAH_EDGE_TAB_SLIM_HIT : SARAH_EDGE_TAB_W
            const h = slot.slim ? SARAH_EDGE_TAB_SLIM_HIT : SARAH_EDGE_TAB_H
            // The collapsed root sits flush right with its bottom at rest; the
            // dock translates from there to the slot.
            nextSlot = { ...slot, dx: slot.side === 'left' ? -(vw - w) : 0, dy: slot.top - (restBottom - h) }
          }
          next = nextSlot ? { mode: 'rest' } : { mode: 'tucked' }
        }
      }
      collapsedRef.current = nextCollapsed
      setCollapsed(nextCollapsed)
      setTabSlot((prev) =>
        prev && nextSlot && prev.side === nextSlot.side && prev.dx === nextSlot.dx && prev.dy === nextSlot.dy && prev.slim === nextSlot.slim
          ? prev
          : nextSlot,
      )
      setPlacement((prev) => (samePlacement(prev, next) ? prev : next))
    }
    // Public Look recheck on PR 864 (Sumpter 375, Payment table): mid-scroll
    // the disc sat on the HOA value until scrolling settled. Every scroll
    // frame, and after each placement commits, the painted launcher is checked
    // with the same point-grid test the cookie cog uses; if it covers text or
    // a control it is hidden in that same frame until a clear spot is found.
    const guard = () => {
      const root = rootRef.current
      if (!root) return
      root.style.visibility = ''
      const parts = Array.from(root.querySelectorAll<HTMLElement>('[data-sarah="dock"], :scope > *'))
      const covers = parts.some((el) => {
        const b = el.getBoundingClientRect()
        if (b.width <= 0 || b.height <= 0) return false
        return boxCoversContent(document, root, { left: b.left - 4, top: b.top - 4, right: b.right + 4, bottom: b.bottom + 4 })
      })
      root.style.visibility = covers ? 'hidden' : ''
    }
    let guardId = 0
    const placeThenGuard = () => {
      place()
      if (!guardId) {
        guardId = window.requestAnimationFrame(() => {
          guardId = 0
          guard()
        })
      }
    }
    const schedule = () => {
      if (!rafId) rafId = window.requestAnimationFrame(placeThenGuard)
    }
    const onScroll = () => {
      guard()
      setScrolling(true)
      window.clearTimeout(settleId)
      settleId = window.setTimeout(() => {
        setScrolling(false)
        schedule()
      }, SARAH_SCROLL_SETTLE_MS)
    }
    const listen = { passive: true } as const
    window.addEventListener('scroll', onScroll, listen)
    window.addEventListener('resize', schedule, listen)
    window.addEventListener('load', schedule, listen)
    window.addEventListener('orientationchange', schedule, listen)
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule)
    ro?.observe(document.body)
    // Re-place once layout settles after content changes too: carousel slide
    // changes (inner scroll, transitions, counter text), lazy content, banners.
    let settleContentId = 0
    const settleContent = () => {
      window.clearTimeout(settleContentId)
      settleContentId = window.setTimeout(schedule, SARAH_SCROLL_SETTLE_MS)
    }
    const onInnerScroll = (e: Event) => {
      if (e.target === document || e.target === window) return
      settleContent()
    }
    document.addEventListener('scroll', onInnerScroll, { capture: true, passive: true })
    document.addEventListener('transitionend', settleContent, true)
    document.addEventListener('animationend', settleContent, true)
    const mo =
      typeof MutationObserver === 'undefined'
        ? null
        : new MutationObserver((records) => {
            const root = rootRef.current
            if (records.some((r) => !(root && root.contains(r.target)))) settleContent()
          })
    mo?.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'src'],
    })
    schedule()
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', schedule)
      window.removeEventListener('load', schedule)
      window.removeEventListener('orientationchange', schedule)
      ro?.disconnect()
      mo?.disconnect()
      document.removeEventListener('scroll', onInnerScroll, { capture: true })
      document.removeEventListener('transitionend', settleContent, true)
      document.removeEventListener('animationend', settleContent, true)
      window.clearTimeout(settleContentId)
      window.clearTimeout(settleId)
      if (rafId) window.cancelAnimationFrame(rafId)
      if (guardId) window.cancelAnimationFrame(guardId)
      if (rootRef.current) rootRef.current.style.visibility = ''
    }
  }, [hidden, panelOpen, pathname, teaserOn, minimized, stable])

  useEffect(() => {
    if (!offer) {
      setTeaserOn(false)
      shownRef.current = false
      return
    }
    const id = window.setTimeout(() => setTeaserOn(true), SARAH_TEASER_DELAY_MS)
    return () => window.clearTimeout(id)
  }, [offer, pathname])

  useEffect(() => {
    if (!teaserOn || !offer || shownRef.current) return
    shownRef.current = true
    trackEvent('sarah_teaser_shown', { surface: 'listing', cta: teaserKind })
  }, [teaserOn, offer, teaserKind])

  // C1: read the minimized choice after mount so SSR and hydration match.
  useEffect(() => {
    if (readLauncherMinimized()) setMinimized(true)
  }, [])

  useEffect(() => {
    minimizedRef.current = minimized
  }, [minimized])

  useEffect(() => {
    if (minimized && focusTabRef.current) {
      focusTabRef.current = false
      restoreTabRef.current?.focus()
    }
  }, [minimized])

  const minimizeLauncher = useCallback(() => {
    writeLauncherMinimized()
    focusTabRef.current = true
    setMinimized(true)
    trackEvent('sarah_launcher_dismiss', { surface: 'launcher' })
  }, [])

  const restoreLauncher = useCallback(() => {
    clearLauncherMinimized()
    setMinimized(false)
    trackEvent('sarah_launcher_restore', { surface: 'launcher' })
  }, [])

  const dismissTeaser = useCallback(() => {
    persistTeaserDismissed(sessionStore())
    setDismissed(true)
    setTeaserOn(false)
  }, [])

  // GA4 sarah_chat_start: the first panel open per page load, so a visitor
  // who closes and reopens the same chat counts once.
  const chatStartedRef = useRef(false)

  const openPanel = useCallback(
    (fromTeaser: boolean) => {
      if (fromTeaser) {
        trackEvent('sarah_teaser_clicked', { surface: 'listing', cta: teaserKind })
        dismissTeaser()
      }
      if (!chatStartedRef.current) {
        chatStartedRef.current = true
        trackEvent('sarah_chat_start', { source: fromTeaser ? 'teaser' : 'launcher' })
      }
      setPanelOpen(true)
      const sid = visitorSessionId()
      if (sid) {
        void recordSarahFunnelStage({ sessionId: sid, stage: 'opened', pageUrl: pathname })
      }
    },
    [dismissTeaser, pathname, teaserKind],
  )

  const launcherRef = useRef<HTMLButtonElement | null>(null)
  const closePanel = useCallback(() => {
    setSheetDy(0)
    dragRef.current = null
    voiceRef.current?.stop()
    voiceRef.current = null
    voiceStartingRef.current = false
    setVoiceStatus('idle')
    setVoiceMuted(false)
    setCaption('')
    setPanelOpen(false)
    // UX review F17: closing returns focus to the button that opened it.
    requestAnimationFrame(() => launcherRef.current?.focus())
  }, [])

  useEffect(() => {
    if (!panelOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [panelOpen, closePanel])

  /** Log the objection and reset the voice UI once the stop result is known. */
  const finishObjection = useCallback(
    (stopped: boolean, via: 'voice' | 'typed'): string => {
      voiceRef.current = null
      voiceStartingRef.current = false
      setVoiceStatus('idle')
      setVoiceMuted(false)
      setCaption('')
      void logWebRecordingObjection({
        sessionId: visitorSessionId(),
        stopped,
        via,
        phone: flowRef.current.answers.phone ?? null,
        personId: personIdRef.current,
        pageUrl: pathname,
      })
      return stopped ? WEB_VOICE_OBJECTION_STOPPED_EN : WEB_VOICE_OBJECTION_FAILED_EN
    },
    [pathname],
  )

  /**
   * Recording objection (stop_recording tool or typed/heard objection). Ends
   * voice first: mic tracks stopped and the xAI socket closed.
   * - live voice: returns the confirmation (only when the stop is confirmed)
   * - voice still starting: returns 'pending'; the start flow stops it and
   *   posts the line once the stop result is known
   * - no voice: logs and returns null
   * Deletes nothing.
   */
  const endVoiceForObjection = useCallback(
    (via: 'voice' | 'typed'): string | 'pending' | null => {
      noRecordRef.current = true
      clearSarahVoiceConsent()
      setVoiceConsentOpen(false)
      const handle = voiceRef.current
      if (handle) return finishObjection(handle.endForObjection(), via)
      if (voiceStartingRef.current) {
        pendingObjectionRef.current = via
        return 'pending'
      }
      void logWebRecordingObjection({
        sessionId: visitorSessionId(),
        stopped: true,
        via,
        phone: flowRef.current.answers.phone ?? null,
        personId: personIdRef.current,
        pageUrl: pathname,
      })
      return null
    },
    [finishObjection, pathname],
  )

  const acceptVisitor = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return
      const guard = evaluateSarahTurn({ visitorText: trimmed, greeted })
      setGreeted(true)
      const hardStop =
        guard.mode === 'wrap-has-agent' || guard.mode === 'stop-recording' || guard.mode === 'escalate'
      const intentHit = matchIntentChip(trimmed)
      if (guard.mode === 'stop-recording') {
        const confirm = endVoiceForObjection(voiceRef.current || voiceStartingRef.current ? 'voice' : 'typed')
        setLines((prev) =>
          confirm === 'pending'
            ? [...prev, { from: 'visitor', text: trimmed }]
            : [...prev, { from: 'visitor', text: trimmed }, { from: 'sarah', text: confirm ?? guard.reply ?? WEB_HELP_EN }],
        )
        return
      }
      if (hardStop || (guard.mode === 'refuse' && !intentHit && flow.step === 'intent')) {
        const reply =
          guard.reply ??
          (guard.mode === 'escalate'
            ? "I'll have a licensed broker call you as soon as possible. What's the best number?"
            : WEB_HELP_EN)
        setLines((prev) => [...prev, { from: 'visitor', text: trimmed }, { from: 'sarah', text: reply }])
        return
      }
      // Answer-first (Muse audit C1): a question the site already answers gets
      // the published answer before any qualifying question. The flow does not
      // move, so a funnel step in progress picks up where it was.
      const site = answerSiteQuestion(trimmed, { pathname })
      if (site) {
        setLines((prev) => [...prev, { from: 'visitor', text: trimmed }, { from: 'sarah', text: site.reply }])
        if (voiceRef.current) {
          skipVoiceSarahRef.current = true
          voiceRef.current.speakExact(site.reply)
        }
        return
      }
      if (looksLikeNavigate(trimmed)) {
        const dest = resolveSarahNavigate(trimmed)
        if (dest) {
          skipVoiceSarahRef.current = true
          setChips(null)
          setLines((prev) => [...prev, { from: 'visitor', text: trimmed }, { from: 'sarah', text: dest.reply }])
          router.push(dest.href)
          voiceRef.current?.speakExact(dest.voice)
          return
        }
      }
      if (looksLikeHomeSearch(trimmed) || (homeRef.current && homeRef.current.step !== 'done')) {
        skipVoiceSarahRef.current = true
        setChips(null)
        setLines((prev) => [...prev, { from: 'visitor', text: trimmed }])
        if (looksLikeHomeSearch(trimmed)) {
          void answerSarahChat({ pagePath: pathname ?? '', question: trimmed }).then((result) => {
            const reply = result.data?.reply ?? listingUnknownFactEn(desk.firstName)
            homeRef.current = result.data?.homeSearch ?? null
            setChips(result.data?.chips ?? null)
            setLines((prev) => [
              ...prev,
              {
                from: 'sarah',
                text: reply,
                cards: result.data?.cards,
                seeAll: result.data?.seeAll,
              },
            ])
            if (result.data?.voiceSummary) voiceRef.current?.speakExact(result.data.voiceSummary)
          })
          return
        }
        const turn = applyHomeSearchTurn(homeRef.current!, trimmed, desk.firstName)
        homeRef.current = turn.next
        setChips(turn.chips)
        setLines((prev) => [...prev, { from: 'sarah', text: turn.reply, cards: turn.cards, seeAll: turn.seeAll }])
        if (turn.voiceSummary) voiceRef.current?.speakExact(turn.voiceSummary)
        if (turn.capture) {
          void captureSarahLead(withPostedGaIds({
            intent: 'buyer',
            answers: leadAnswersFor(homeSearchFlowAnswers(turn.next), noRecordRef.current),
          })).then((res) => {
            if (res?.data?.personId) personIdRef.current = res.data.personId
          })
        }
        return
      }
      if (
        shouldAnswerFromPage({
          pathname,
          visitorText: trimmed,
          flowStep: flowRef.current.step,
        })
      ) {
        setChips(null)
        setLines((prev) => [...prev, { from: 'visitor', text: trimmed }])
        void answerSarahChat({ pagePath: pathname ?? '', question: trimmed }).then((result) => {
          const reply = result.data?.reply ?? listingUnknownFactEn(desk.firstName)
          const follow = afterHelpfulAnswer(flowRef.current, desk.firstName)
          if (follow) {
            setFlow(follow.next)
            flowRef.current = follow.next
            setChips(follow.chips)
            setLines((prev) => [...prev, { from: 'sarah', text: reply }, { from: 'sarah', text: follow.reply }])
            return
          }
          setLines((prev) => [...prev, { from: 'sarah', text: reply }])
        })
        return
      }
      const spoken = voiceStatus !== 'idle'
      const turn = applyIntentTurn(flowRef.current, trimmed, desk.firstName, spoken ? 'spoken' : 'typed')
      setFlow(turn.next)
      flowRef.current = turn.next
      setChips(turn.chips)
      setLines((prev) => [...prev, { from: 'visitor', text: trimmed }, { from: 'sarah', text: turn.reply }])
      const sid = visitorSessionId()
      if (sid && turn.funnel) {
        void recordSarahFunnelStage({
          sessionId: sid,
          stage: turn.funnel,
          intent: turn.next.intent,
          pageUrl: pathname,
        })
      }
      if (turn.capture) {
        void captureSarahLead(withPostedGaIds({
          intent: turn.next.intent ?? 'how',
          answers: leadAnswersFor(turn.next.answers, noRecordRef.current),
          startCma: turn.startCma,
        })).then((res) => {
          if (res?.data?.personId) personIdRef.current = res.data.personId
        })
      }
      if (turn.funnel === 'consent' && turn.next.answers.phone) {
        const consentChannel = spoken ? 'spoken' : 'typed'
        const decided = decideSarahConsent(trimmed, consentChannel)
        void recordSarahConsent({
          // After a recording objection, keep only the yes/no, not the words.
          reply: noRecordRef.current ? (decided ? (decided.granted ? 'yes' : 'no') : '') : trimmed,
          channel: consentChannel,
          question: turn.consentQuestion ?? '',
          phone: turn.next.answers.phone,
          email: turn.next.answers.email,
          pageUrl: pathname,
          sessionId: sid,
        })
      }
    },
    [desk.firstName, endVoiceForObjection, greeted, pathname, router, voiceStatus],
  )

  const goToPage = useCallback(
    (href: string) => {
      if (!isPublicSarahHref(href)) return
      skipVoiceSarahRef.current = true
      router.push(href)
      voiceRef.current?.speakExact(SARAH_NAVIGATE_VOICE)
    },
    [router],
  )

  const send = useCallback(() => {
    const text = draft.trim()
    if (!text) return
    setDraft('')
    acceptVisitor(text)
  }, [acceptVisitor, draft])

  const tapChip = useCallback(
    (label: string) => {
      trackEvent('click_cta', { cta: 'sarah_intent', action: label, surface: desk.slug })
      acceptVisitor(label)
    },
    [acceptVisitor, desk.slug],
  )

  const continueVoiceAfterArm = useCallback(
    (armed: SarahArmedMic) => {
      trackEvent('click_cta', { cta: 'sarah_voice', action: 'start', surface: desk.slug })
      setVoiceStatus('asking')
      // Objection while starting: release the mic, never connect, then post
      // the line for the confirmed result.
      const settlePending = (stopped: boolean): boolean => {
        const via = pendingObjectionRef.current
        if (!noRecordRef.current) return false
        pendingObjectionRef.current = null
        if (via) {
          const line = finishObjection(stopped, via)
          setLines((prev) => [...prev, { from: 'sarah', text: line }])
        } else {
          voiceStartingRef.current = false
          setVoiceStatus('idle')
        }
        return true
      }
      void (async () => {
        try {
          const { token } = await continueSarahVoiceAfterArm(armed, mintSarahVoiceToken)
          if (noRecordRef.current) {
            releaseSarahArmedMic(armed)
            settlePending(true)
            return
          }
          if (!token.data) {
            voiceStartingRef.current = false
            setVoiceStatus('error')
            releaseSarahArmedMic(armed)
            if (takeVoiceErrorSlot(voiceErrorPostedRef.current)) {
              setLines((prev) => [...prev, { from: 'sarah', text: token.error ?? WEB_VOICE_START_FAIL_EN }])
            }
            return
          }
          const handle = await startSarahVoiceSession({
            secret: token.data.secret,
            brokerFirstName: token.data.brokerFirstName,
            armed,
            onStatus: setVoiceStatus,
            onRecordingObjection: () => {
              const confirm = endVoiceForObjection('voice')
              if (confirm && confirm !== 'pending') setLines((prev) => [...prev, { from: 'sarah', text: confirm }])
            },
            onError: (message) => {
              if (!takeVoiceErrorSlot(voiceErrorPostedRef.current)) return
              setLines((prev) => [...prev, { from: 'sarah', text: message }])
            },
            onTranscript: (from, text, meta) => {
              if (!meta?.interim) setCaption(text)
              if (
                from === 'visitor' &&
                !meta?.interim &&
                (looksLikeHomeSearch(text) || (homeRef.current && homeRef.current.step !== 'done'))
              ) {
                acceptVisitor(text)
                return
              }
              if (from === 'sarah' && skipVoiceSarahRef.current) {
                skipVoiceSarahRef.current = false
                return
              }
              setLines((prev) => {
                const last = prev[prev.length - 1]
                if (meta?.interim && last?.voice && last.from === from) {
                  return [...prev.slice(0, -1), { from, text, voice: true }]
                }
                if (last?.voice && last.from === from && last.text === text) return prev
                return [...prev, { from, text, voice: true }]
              })
            },
          })
          if (noRecordRef.current) {
            // Objection arrived while voice was starting: never go live.
            settlePending(handle.endForObjection())
            return
          }
          voiceRef.current = handle
          voiceStartingRef.current = false
        } catch (err) {
          if (noRecordRef.current) {
            releaseSarahArmedMic(armed)
            settlePending(true)
            return
          }
          voiceStartingRef.current = false
          setVoiceStatus('error')
          logSarahVoiceError(err)
          if (takeVoiceErrorSlot(voiceErrorPostedRef.current)) {
            setLines((prev) => [...prev, { from: 'sarah', text: sarahVoiceErrorMessage(err) }])
          }
        }
      })()
    },
    [acceptVisitor, desk.slug, endVoiceForObjection, finishObjection],
  )

  const endVoice = useCallback(() => {
    voiceRef.current?.stop()
    voiceRef.current = null
    voiceStartingRef.current = false
    setVoiceStatus('idle')
    setVoiceMuted(false)
    setCaption('')
  }, [])

  const toggleMute = useCallback(() => {
    const next = !voiceMuted
    setVoiceMuted(next)
    voiceRef.current?.setMuted(next)
  }, [voiceMuted])

  /**
   * The only path to the microphone: an explicit tap on the composer mic. The
   * panel opens text-first, so opening Sarah never asks for permission. iOS
   * still needs getUserMedia inside this tap (beginSarahVoiceFromMicTap).
   */
  /** Composer mic: opens the consent step. Never touches the microphone. */
  const openVoiceConsent = useCallback(() => {
    clearSarahVoiceConsent()
    if (noRecordRef.current) {
      setLines((prev) => [...prev, { from: 'sarah', text: WEB_VOICE_OFF_FOR_CHAT_EN }])
      return
    }
    setVoiceConsentOpen(true)
  }, [])

  const startTalkFromMicTap = useCallback(() => {
    if (voiceRef.current || voiceStartingRef.current) return
    voiceStartingRef.current = true
    setVoiceMuted(false)
    const started = beginSarahVoiceFromMicTap()
    if (!started.ok) {
      voiceStartingRef.current = false
      setVoiceStatus('error')
      if (takeVoiceErrorSlot(voiceErrorPostedRef.current)) {
        setCaption(started.message)
        setLines((prev) => [...prev, { from: 'sarah', text: started.message }])
      }
      return
    }
    continueVoiceAfterArm(started.armed)
  }, [continueVoiceAfterArm])

  const onSheetPointerDown = useCallback(
    (e: PointerEvent<HTMLElement>) => {
      if (wide) return
      const handle = (e.target as HTMLElement).closest('[data-sarah-drag]')
      if (!handle) return
      dragRef.current = { y: e.clientY, dy: 0 }
      e.currentTarget.setPointerCapture(e.pointerId)
    },
    [wide],
  )

  const onSheetPointerMove = useCallback((e: PointerEvent<HTMLElement>) => {
    if (!dragRef.current) return
    const dy = Math.max(0, e.clientY - dragRef.current.y)
    dragRef.current.dy = dy
    setSheetDy(dy)
  }, [])

  const onSheetPointerUp = useCallback(() => {
    if (!dragRef.current) return
    const dy = dragRef.current.dy
    dragRef.current = null
    if (dy > 96) {
      closePanel()
      return
    }
    setSheetDy(0)
  }, [closePanel])

  if (hidden) return null

  const showBubble = teaserOn && offer && teaserKind === 'bubble'
  const asTab = !stable && (minimized || collapsed)
  const showBadge = teaserOn && offer && teaserKind === 'badge'
  const voiceLive =
    voiceStatus === 'asking' ||
    voiceStatus === 'connecting' ||
    voiceStatus === 'listening' ||
    voiceStatus === 'thinking' ||
    voiceStatus === 'speaking'
  const micLabel =
    voiceStatus === 'listening'
      ? 'Listening. Stop talking with Sarah'
      : voiceStatus === 'connecting' || voiceStatus === 'asking'
        ? 'Connecting to Sarah voice'
        : voiceStatus === 'thinking'
          ? 'Sarah is thinking. Stop voice'
          : voiceStatus === 'speaking'
            ? 'Sarah is speaking. Stop voice'
            : 'Talk to Sarah'
  const voiceHint =
    voiceStatus === 'speaking'
      ? WEB_VOICE_HINT_SPEAK_EN
      : voiceStatus === 'thinking'
        ? WEB_VOICE_HINT_THINK_EN
        : voiceLive
          ? WEB_VOICE_HINT_LISTEN_EN
          : WEB_VOICE_HINT_END_EN
  const lift = placement.mode === 'lifted' ? placement.lift : 0
  const slotOn = collapsed && !minimized && !panelOpen && tabSlot != null

  return (
    <div className={V3_ROOT_CLASS}>
      <div
        ref={rootRef}
        className="v3-sarah"
        data-sarah="root"
        data-sarah-open={panelOpen ? '1' : '0'}
        data-sarah-avoid={placement.mode}
        data-sarah-state={panelOpen ? 'full' : !asTab ? 'full' : minimized ? 'minimized' : 'collapsed'}
        data-sarah-scrolling={scrolling ? '1' : '0'}
        data-sarah-stable={stable ? '1' : '0'}
        data-sarah-edge={slotOn ? tabSlot.side : undefined}
        data-sarah-tab-size={slotOn ? (tabSlot.slim ? 'slim' : 'full') : undefined}
        style={
          slotOn
            ? ({
                '--v3-sarah-tab-x': `${tabSlot.dx}px`,
                '--v3-sarah-tab-y': `${tabSlot.dy}px`,
              } as CSSProperties)
            : lift
              ? ({ '--v3-sarah-lift': `${lift}px` } as CSSProperties)
              : undefined
        }
      >
        {panelOpen && !wide ? (
          <button
            type="button"
            className="v3-sarah__scrim"
            aria-label="Close chat"
            data-sarah="scrim"
            onClick={closePanel}
          />
        ) : null}

        {panelOpen ? (
          <section
            className="v3-sarah__panel"
            data-sarah="panel"
            data-clarity-mask="True"
            data-sarah-surface={wide ? 'card' : 'sheet'}
            data-sarah-broker={desk.slug}
            id={panelId}
            aria-labelledby={titleId}
            style={sheetDy ? { transform: `translateY(${sheetDy}px)` } : undefined}
            onPointerDown={onSheetPointerDown}
            onPointerMove={onSheetPointerMove}
            onPointerUp={onSheetPointerUp}
            onPointerCancel={onSheetPointerUp}
          >
            <div className="v3-sarah__sheet-handle" data-sarah-drag="">
              <span />
            </div>
            <div className="v3-sarah__panel-head" data-sarah-drag="">
              <SarahFace size={36} />
              <div className="v3-sarah__panel-titles">
                <div className="v3-sarah__panel-name" id={titleId}>
                  {SARAH_NAME}
                </div>
              </div>
              <button
                type="button"
                className="v3-sarah__dismiss"
                aria-label="Close chat"
                onClick={closePanel}
              >
                ×
              </button>
            </div>
            <div className="v3-sarah__messages" ref={messagesRef} data-sarah="messages">
              {lines.map((line, i) => (
                <div
                  key={`${line.from}-${i}`}
                  className={
                    line.from === 'sarah' ? 'v3-sarah__msg v3-sarah__msg--sarah' : 'v3-sarah__msg v3-sarah__msg--visitor'
                  }
                >
                  {line.from === 'sarah' ? <SarahFace size={28} /> : null}
                  <div className="v3-sarah__msg-stack">
                    <p className="v3-sarah__msg-body">{line.text}</p>
                    {line.from === 'sarah' && (line.cards?.length || line.seeAll) ? (
                      <SarahHomeSearchCards
                        cards={line.cards ?? []}
                        seeAll={line.seeAll}
                        onNavigate={goToPage}
                      />
                    ) : null}
                    {line.notice ? (
                      <p className="v3-sarah__saved" data-sarah="saved-notice" data-sarah-disclosure="">
                        {line.notice}
                      </p>
                    ) : null}
                  </div>
                </div>
              ))}
              <div ref={latestRef} data-sarah="latest" />
            </div>
            {chips && chips.length > 0 && !voiceLive ? (
              <div className="v3-sarah__hints" data-sarah="intent-chips">
                {flow.step === 'intent' ? (
                  <p className="v3-sarah__hints-kicker">Here&apos;s a starting point</p>
                ) : null}
                <div className="v3-sarah__hint-row">
                  {chips.map((chip) => (
                    <button
                      key={chip.id}
                      type="button"
                      className="v3-sarah__hint"
                      data-sarah-chip={chip.id}
                      onClick={() => tapChip(chip.label)}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
            {voiceConsentOpen && !voiceLive ? (
              <div className="v3-sarah__voice-consent" data-sarah="voice-consent" role="group" aria-label="Voice chat consent">
                <p className="v3-sarah__saved" data-sarah-disclosure="">{WEB_VOICE_NOTICE_EN}</p>
                <div className="v3-sarah__voice-actions">
                  <button
                    type="button"
                    className="v3-sarah__voice-start"
                    data-sarah="voice-start"
                    onClick={() => {
                      grantSarahVoiceConsent()
                      startTalkFromMicTap()
                    }}
                  >
                    {WEB_VOICE_START_EN}
                  </button>
                  <button
                    type="button"
                    className="v3-sarah__voice-type"
                    data-sarah="voice-type-instead"
                    onClick={() => {
                      clearSarahVoiceConsent()
                      setVoiceConsentOpen(false)
                    }}
                  >
                    {WEB_VOICE_TYPE_INSTEAD_EN}
                  </button>
                </div>
              </div>
            ) : null}
            {voiceLive ? (
              <div
                className="v3-sarah__voice-stage"
                data-sarah="voice-stage"
                data-sarah-voice={voiceMuted ? 'muted' : voiceStatus}
              >
                <p className="v3-sarah__caption" data-sarah="caption" aria-live="polite">
                  {caption || (voiceMuted ? 'Muted' : voiceHint)}
                </p>
                <p className="v3-sarah__voice-hint" data-sarah="voice-hint">
                  {`${voiceHint}. ${WEB_VOICE_HINT_END_EN}`}
                </p>
                <div className="v3-sarah__voice-actions">
                  <button
                    type="button"
                    className="v3-sarah__mute"
                    data-sarah="mute"
                    aria-pressed={voiceMuted}
                    onClick={toggleMute}
                  >
                    {voiceMuted ? 'Unmute' : 'Mute'}
                  </button>
                  <button
                    type="button"
                    className="v3-sarah__end"
                    data-sarah="end"
                    aria-label="End talk"
                    onClick={endVoice}
                  >
                    ×
                  </button>
                </div>
              </div>
            ) : null}
            <form
              data-clarity-mask="True"
              className="v3-sarah__composer"
              onSubmit={(e) => {
                e.preventDefault()
                send()
              }}
            >
              <input
                type="text"
                name="sarah-message"
                aria-label="Message Sarah"
                autoComplete="off"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onFocus={() => scrollToLatest()}
              />
              <button
                type="button"
                className="v3-sarah__mic"
                data-sarah="mic"
                data-sarah-voice={voiceMuted ? 'muted' : voiceStatus}
                aria-label={micLabel}
                aria-pressed={voiceLive}
                onClick={() => {
                  if (voiceLive) endVoice()
                  else openVoiceConsent()
                }}
              >
                <Mic aria-hidden="true" strokeWidth={2} />
              </button>
              <button type="submit">Send</button>
            </form>
          </section>
        ) : null}

        {showBubble && !asTab ? (
          <div className="v3-sarah__teaser" data-sarah-teaser="bubble" role="status">
            <button
              type="button"
              className="v3-sarah__teaser-open"
              onClick={() => openPanel(true)}
            >
              <span className="v3-sarah__teaser-photo">
                <SarahFace size={36} />
              </span>
              <span>{SARAH_TEASER_COPY}</span>
            </button>
            <button
              type="button"
              className="v3-sarah__dismiss"
              aria-label="Dismiss"
              data-sarah-teaser-dismiss=""
              onClick={dismissTeaser}
            >
              ×
            </button>
          </div>
        ) : null}

        <div className="v3-sarah__dock" data-sarah="dock">
          {asTab && !panelOpen ? (
            <button
              type="button"
              ref={restoreTabRef}
              className="v3-sarah__restore"
              data-sarah="launcher-restore"
              data-sarah-tab={minimized ? 'minimized' : 'collapsed'}
              aria-label={minimized ? 'Show Sarah chat' : 'Chat with Sarah'}
              onClick={() => {
                // A collapsed tab (no clear spot) opens chat; a minimized one
                // brings the disc back, as the visitor chose.
                if (minimized) restoreLauncher()
                else openPanel(false)
              }}
            >
              <span className="v3-sarah__restore-face">
                <SarahFace size={28} />
              </span>
            </button>
          ) : (
            <>
              {!panelOpen && !stable ? (
                <button
                  type="button"
                  className="v3-sarah__launcher-close"
                  data-sarah="launcher-dismiss"
                  aria-label="Hide Sarah chat"
                  onClick={minimizeLauncher}
                >
                  <span aria-hidden="true">×</span>
                </button>
              ) : null}
              <button
                type="button"
                ref={launcherRef}
                className="v3-sarah__launcher"
                data-sarah="launcher"
                aria-label="Chat with Sarah"
                aria-expanded={panelOpen}
                aria-controls={panelOpen ? panelId : undefined}
                onClick={() => {
                  if (panelOpen) closePanel()
                  else openPanel(showBadge)
                }}
              >
                <SarahFace className="v3-sarah__avatar" size={80} alt={SARAH_AVATAR_ALT} />
                <SarahLauncherVideo paused={panelOpen} />
                {showBadge ? <span className="v3-sarah__badge" data-sarah-teaser="badge" /> : null}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/** One fixed bottom-right disc at every width (Matt 2026-10-22). */
export const SARAH_LAUNCHER_STABLE: boolean = true
